/**
 * CitationStyleService.js
 *
 * Integrates citeproc-js to power CSL-based citation formatting.
 * Handles:
 *  - Fetching citation style metadata from backend API
 *  - Fetching CSL XML from backend API (no more direct GitHub calls)
 *  - Mapping internal reference objects to CSL-JSON
 *  - Generating in-text citation strings
 *  - Rendering bibliography entries as HTML
 *
 * NOTE: localStorage caching for CSL XML has been removed.
 *       XML is cached in-memory only for the current session.
 */

import CSL from 'citeproc';
import { enUSLocale } from './Locales';
import { getCitationStyles, getCitationStyleXML } from './apiController';

// ─── In-memory cache for style XML ──────────────────────────────────────────
// Key: styleId, Value: XML string
const styleXMLCache = {};

// ─── Metadata cache for styles we've seen (used for dependent style redirection)
const seenStylesMetadata = new Map();

// ─── localStorage key for active style ──────────────────────────────────────
const ACTIVE_STYLE_KEY = 'csl_active_style';

// ─── Default style (used when nothing is set yet) ───────────────────────────
const DEFAULT_STYLE = { id: 'ieee', label: 'IEEE', title: 'IEEE' };

/**
 * Fetches citation styles from the backend matching a search query.
 *
 * @param {string} [searchQuery]
 * @returns {Promise<Array>}
 */
export async function fetchCitationStyles(searchQuery) {
  try {
    const response = await getCitationStyles(searchQuery);
    
    let styles = [];
    if (response && Array.isArray(response.data)) {
      styles = response.data;
    } else if (Array.isArray(response)) {
      styles = response;
    }

    // Update the metadata cache so fetchStyleXML knows about parent styles
    styles.forEach(s => {
      if (s.citation_id) seenStylesMetadata.set(s.citation_id, s);
    });

    return styles;
  } catch (err) {
    console.error('Failed to fetch citation styles:', err);
    return [];
  }
}


// ─── Fetch & cache CSL XML (from backend, in-memory only) ──────────────────
/**
 * Fetches the CSL XML for a given style from the backend.
 * Caches in memory (no localStorage).
 * 
 * If the style is dependent, it automatically fetches the parent style's XML
 * but caches it under the requested styleId.
 *
 * @param {string} styleId
 * @returns {Promise<string>} CSL XML string
 */
export async function fetchStyleXML(styleId) {
  // Return from in-memory cache if available
  if (styleXMLCache[styleId]) return styleXMLCache[styleId];

  // Check if this is a dependent style that needs a parent's XML
  let idToFetch = styleId;
  let styleMeta = seenStylesMetadata.get(styleId);

  // If we don't have metadata yet, try to fetch it first so we know if it's dependent
  if (!styleMeta) {
    const freshStyles = await fetchCitationStyles(styleId);
    styleMeta = freshStyles.find(s => s.citation_id === styleId);
  }

  if (styleMeta?.is_dependent && styleMeta.parent_style) {
    idToFetch = styleMeta.parent_style;
    console.log(`Redirecting fetch for dependent style ${styleId} to parent ${idToFetch}`);
  }

  const response = await getCitationStyleXML(idToFetch);
  // The backend may return raw XML string or an object with a data/xml field
  let xml;
  if (typeof response === 'string') {
    xml = response;
  } else if (response?.data && typeof response.data === 'string') {
    xml = response.data;
  } else if (response?.xml && typeof response.xml === 'string') {
    xml = response.xml;
  } else {
    throw new Error(`Unexpected response format for CSL style "${idToFetch}"`);
  }

  styleXMLCache[styleId] = xml;
  return xml;
}

/**
 * Get the cached XML synchronously (for places that can't await).
 * Returns null if not yet fetched.
 *
 * @param {string} styleId
 * @returns {string|null}
 */
export function getCachedStyleXML(styleId) {
  return styleXMLCache[styleId] || null;
}

// ─── Persist / retrieve active style ─────────────────────────────────────────
export function getActiveStyle() {
  const stored = localStorage.getItem(ACTIVE_STYLE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      /* ignore */
    }
  }
  return DEFAULT_STYLE;
}

export function setActiveStyle(styleObj) {
  localStorage.setItem(ACTIVE_STYLE_KEY, JSON.stringify(styleObj));
}

// ─── Map internal reference → CSL-JSON item ──────────────────────────────────
export function mapToCSLItem(ref) {
  // Parse "Smith, J., & Doe, A." into CSL name objects
  const parseAuthors = (authorsStr) => {
    if (!authorsStr) return [{ literal: 'Unknown' }];
    return authorsStr.split(/,\s*&\s*|;\s*/).map((a) => {
      const parts = a.trim().split(',');
      if (parts.length >= 2) {
        return { family: parts[0].trim(), given: parts[1].trim() };
      }
      return { literal: a.trim() };
    });
  };

  const typeMap = {
    article: 'article-journal',
    book: 'book',
    conference: 'paper-conference',
    website: 'webpage',
    thesis: 'thesis',
    other: 'document',
  };

  const cslItem = {
    id: ref.id,
    type: typeMap[ref.type] || 'document',
    title: ref.title || '',
    author: parseAuthors(ref.authors),
    issued: {
      'date-parts': [[parseInt(ref.year, 10) || new Date().getFullYear()]],
    },
  };

  if (ref.journal)    cslItem['container-title'] = ref.journal;
  if (ref.volume)     cslItem.volume              = ref.volume;
  if (ref.pages)      cslItem.page               = ref.pages;
  if (ref.publisher)  cslItem.publisher           = ref.publisher;
  if (ref.url)        cslItem.URL                = ref.url;
  if (ref.doi)        cslItem.DOI                = ref.doi;

  return cslItem;
}

// ─── Instantiate citeproc engine ─────────────────────────────────────────────
function buildEngine(styleXML, references) {
  const itemsMap = {};
  references.forEach((ref) => {
    itemsMap[ref.id] = mapToCSLItem(ref);
  });

  const sys = {
    retrieveLocale: () => {
      return enUSLocale;
    },
    retrieveItem: (id) => itemsMap[id],
  };

  return new CSL.Engine(sys, styleXML);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Generate grouped in-text citation strings using a single engine.
 *
 * @param {object[]} clusters   – array of { citationId, refIds }
 * @param {string}   styleXML   – CSL XML string
 * @param {object[]} allRefs    – all available references
 * @returns {string[]|null}     – array of text strings for each cluster, or null on error
 */
export function generateGroupedInTextCitations(clusters, styleXML, allRefs) {
  if (!styleXML || !CSL || clusters.length === 0) return null;

  try {
    const engine = buildEngine(styleXML, allRefs);
    engine.updateItems(allRefs.map((r) => r.id));

    const results = [];
    const citationsPre = [];

    for (let i = 0; i < clusters.length; i++) {
      const cluster = clusters[i];
      const citation = {
        citationID: cluster.citationId,
        citationItems: cluster.refIds.map((id) => ({ id })),
        properties: { noteIndex: 0 },
      };

      const out = engine.processCitationCluster(citation, citationsPre, []);
      const computed = out[1] || [];
      const inserted = computed.find((c) => c[0] === i);
      results.push(inserted ? inserted[1] : '[?]');

      citationsPre.push([citation.citationID, citation.properties.noteIndex]);
    }
    return results;
  } catch (err) {
    console.warn('citeproc grouped in-text generation failed:', err);
    return null;
  }
}

/**
 * Generate a formatted in-text citation string via citeproc.
 * Falls back to numbered format if citeproc isn't available.
 *
 * @param {object[]} selectedRefs  – array of internal reference objects
 * @param {string}   styleXML      – CSL XML string for the active style
 * @returns {string}               – e.g. "(Smith, 2024)" or "[1,2]"
 */
export function generateInTextCitation(selectedRefs, styleXML) {
  if (!styleXML || !CSL) {
    const numbers = selectedRefs.map((r) => r.number || '?');
    return `[${numbers.join(', ')}]`;
  }

  try {
    const engine = buildEngine(styleXML, selectedRefs);
    engine.updateItems(selectedRefs.map((r) => r.id));

    const ids = selectedRefs.map((r) => ({ id: r.id, locator: undefined }));
    const result = engine.makeCitationCluster(ids);
    return result;
  } catch (err) {
    console.warn('citeproc in-text generation failed, using fallback:', err);
    const numbers = selectedRefs.map((r) => r.number || '?');
    return `[${numbers.join(', ')}]`;
  }
}

/**
 * Generate bibliography HTML entries for all references in order.
 *
 * @param {object[]} references    – ordered array of internal reference objects
 * @param {string}   styleXML      – CSL XML string
 * @returns {string[]}             – array of HTML strings, one per reference
 */
export function generateBibliography(references, styleXML) {
  if (!styleXML || !CSL || references.length === 0) return null;

  try {
    const engine = buildEngine(styleXML, references);
    engine.updateItems(references.map((r) => r.id));
    const [params, entries] = engine.makeBibliography();
    return { params, entries };
  } catch (err) {
    console.warn('citeproc bibliography generation failed:', err);
    return null;
  }
}
